import { useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import { Button } from '@zenterprise-inc/ui';
import cn from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { PreloadedQuery, useMutation, usePreloadedQuery, useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';

import { initialSurveyHomeQuery } from '@/__generated__/initialSurveyHomeQuery.graphql';
import { personalDeductionNeedsAuthenticationQuery } from '@/__generated__/personalDeductionNeedsAuthenticationQuery.graphql';
import { submitSurveyMutation } from '@/__generated__/submitSurveyMutation.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { BusinessList } from '@/components/survey/home/BusinessList';
import { HomeIndicator } from '@/components/survey/home/HomeIndicator';
import { surveySubmit } from '@/graphql/mutation/survey/submit';
import { surveyHomeQuery } from '@/graphql/query/survey/init';
import { needsAuthenticationQuery } from '@/graphql/query/tax-refund/personal-deduction';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { currentBusinessBmanTinAtom, surveyAnswerIdAtom } from '@/lib/stores/survey/common';

export const SurveyHomeContainer = ({ queryRef }: { queryRef: PreloadedQuery<initialSurveyHomeQuery> }) => {
  const router = useRouter();
  const { getSurveyBusinessInfo, getSurveyBusinessAnswer } = usePreloadedQuery<initialSurveyHomeQuery>(surveyHomeQuery, queryRef);
  const [submit, isLoading] = useMutation<submitSurveyMutation>(surveySubmit);
  const surveyAnswerId = useAtomValue(surveyAnswerIdAtom);
  const setCurrentBusinessBmanTin = useSetAtom(currentBusinessBmanTinAtom);
  const { showDialog } = useDialog();
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();
  const relayEnvironment = useRelayEnvironment();

  const concatBusinesses = useMemo(
    () =>
      getSurveyBusinessAnswer.businessAnswer
        ?.map((businessAnswer) => {
          const businessInfo = getSurveyBusinessInfo?.business?.find((info) => info.bmanTin === businessAnswer.bmanTin) || {
            bmanTin: businessAnswer.bmanTin,
            totalAmount: 0,
            tnm: '',
            bsno: '',
            tfbDtcsNm: ''
          };
          return {
            bmanTin: businessAnswer.bmanTin || '',
            totalAmount: businessInfo?.totalAmount || 0,
            isCompleted: businessAnswer.isCompleted || false,
            displayBusinessName: businessInfo.tnm || businessInfo.bsno || '',
            industryType: businessInfo.tfbDtcsNm || ''
          };
        })
        .sort((a, b) => {
          if (a.isCompleted !== b.isCompleted) {
            return a.isCompleted ? 1 : -1;
          }
          return b?.totalAmount - a?.totalAmount;
        }) || [],
    [getSurveyBusinessAnswer.businessAnswer, getSurveyBusinessInfo.business]
  );

  const incompleteCount = concatBusinesses.filter(({ isCompleted }) => !isCompleted).length;
  const isCompletedEvery = concatBusinesses.length - incompleteCount === concatBusinesses.length;

  const handleNext = async () => {
    if (isCompletedEvery) {
      sendClickEvent('survey_home_confirm', eventAttributes);

      submit({
        variables: { surveyAnswerId },
        onCompleted: () => {
          fetchQuery<personalDeductionNeedsAuthenticationQuery>(relayEnvironment, needsAuthenticationQuery, {})
            .toPromise()
            .then((res) => {
              if (res?.isCertNeed?.result) {
                // 인적공제 간편인증 필요
                router.push(SURVEY_PAGES.인적공제_간편인증필요);
                return;
              }
              // 인적공제 간편인증 필요 대상 아님
              router.push(SURVEY_PAGES.설문완료);
            })
            .catch(() => {
              showDialog({
                title: '일시적인 오류가 발생했어요.\n잠시 후 다시 시도해주세요.'
              });
            });
        },
        onError: () => {
          showDialog({
            title: '일시적인 오류가 발생했어요.\n잠시 후 다시 시도해주세요.'
          });
        }
      });
    } else {
      sendClickEvent('survey_home_cta-start', eventAttributes);
      // 다음 사업체 설문 진행
      setCurrentBusinessBmanTin(concatBusinesses[0]?.bmanTin || '');
      router.push(SURVEY_PAGES.intro);
    }
  };

  return (
    <>
      <SurveyTopNavigation
        onBackClick={() => {
          sendClickEvent('survey_home_back');

          router.back();
        }}
        rightButton={{
          label: '나가기',
          onClick: () => {
            sendClickEvent('survey_home_exit');

            router.push(COMMON_PATHS.환급조회목록);
          }
        }}
      />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <article>
          <HomeIndicator isCompletedEvery={isCompletedEvery} />
          <section>
            {isCompletedEvery ? (
              <h2 className={common.content_title}>
                모든 사업장에 대한
                <br />
                답변을 완료했어요!
              </h2>
            ) : (
              <h2 className={common.content_title}>
                {concatBusinesses[0]?.displayBusinessName}에 대한
                <br />
                질문을 시작할게요
              </h2>
            )}
            {!isCompletedEvery && <h3 className={common.section_title}>{incompleteCount}개의 사업장 질문이 남았어요</h3>}
            <ul>
              {concatBusinesses.map(({ isCompleted, bmanTin, industryType, displayBusinessName }) => (
                <li
                  key={bmanTin}
                  onClick={() => {
                    if (isCompleted) return;
                    sendClickEvent('survey_home_list-start', eventAttributes);

                    setCurrentBusinessBmanTin(concatBusinesses[0]?.bmanTin || '');
                    router.push(SURVEY_PAGES.intro);
                  }}
                >
                  <BusinessList
                    businessTypeName={industryType}
                    displayName={displayBusinessName}
                    isCompleted={isCompleted}
                    onEditClick={() => {
                      sendClickEvent('survey_home_edit', eventAttributes);

                      setCurrentBusinessBmanTin(bmanTin);
                      router.push(SURVEY_PAGES.intro);
                    }}
                  />
                </li>
              ))}
            </ul>
          </section>
        </article>
        <Button.StickyBottom.Wrapper>
          <div className={'px-4.5 flex w-full flex-col gap-3'}>
            <p className={'body-13 text-center font-normal text-gray-600'}>
              {/* TODO: 양도세 추가로 임시 제거. 추후 서버에서 양도세 값 받아서 종합소득세 + 양도세 금액으로 노출 검토 */}
              답변을 모두 제출하면 환급 검토가 진행돼요
              {/*답변을 모두 제출하면 <span className={'font-bold'}>{numberWithCommas(getSurveyStatus.surveyStatus?.totalAmount || 0)}원</span>의 검토가 진행돼요*/}
            </p>
            <Button.StickyBottom.Button onClick={handleNext} state={'primary'} disabled={isLoading}>
              {isCompletedEvery ? '제출하고 검토 요청하기' : '답변 시작하기'}
            </Button.StickyBottom.Button>
          </div>
        </Button.StickyBottom.Wrapper>
      </main>
    </>
  );
};
